/**
 * Rain-shelter mask over `MapData.rainShelter` rects (world pixels). Pure, no Phaser — unit-tested.
 *
 * A point under a roof gets a `coverage` in 0..1 that rises from the eave inwards: the outermost roof pixel
 * is barely covered and a point `edge` (8) px inside is fully covered. The RainLayer draws a streak pixel
 * only when its per-drop jitter beats the coverage, so drips thin out across the eave and nothing falls
 * under the roof proper. Coverage is computed against the UNION of rects — the seam between the inn's
 * shell roof and its porch rect is not an eave and shows no drips.
 *
 * `eaves` are the exposed bottom edges of the union (the front eave of a roof, the lip of an awning): the
 * row just below each rect minus every span another rect covers. Drips fall from those.
 */
import type { Rect } from './layers';

export const SHELTER_EDGE = 8;

/** Exposed bottom edge of a shelter: the first open row below the roof, from x for w pixels. */
export interface Eave { x: number; y: number; w: number }

export class ShelterMask {
  readonly rects: readonly Rect[];
  readonly eaves: readonly Eave[];
  readonly edge: number;
  constructor(rects: readonly Rect[] = [], edge = SHELTER_EDGE) {
    this.rects = rects.filter((r) => r && r.w > 0 && r.h > 0).map((r) => ({ x: r.x, y: r.y, w: r.w, h: r.h }));
    this.edge = Math.max(1, edge);
    this.eaves = exposedEaves(this.rects);
  }
  get empty(): boolean { return this.rects.length === 0; }

  /** Under any shelter rect (integer pixel test, half-open rects). */
  inside(x: number, y: number): boolean {
    for (const r of this.rects) if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return true;
    return false;
  }

  /**
   * Pixels of roof between (x, y) and open sky along the nearest axis, capped at `edge`; 0 outside.
   * The outermost covered pixel has depth 1 (one pixel to leave the roof), so coverage there is 1/edge.
   */
  depth(x: number, y: number): number {
    if (!this.inside(x, y)) return 0;
    return Math.min(this.run(x, y, 1, 0), this.run(x, y, -1, 0), this.run(x, y, 0, 1), this.run(x, y, 0, -1));
  }

  /** 0 outside → 1 fully under the roof (`edge` px or deeper from every eave). */
  coverage(x: number, y: number): number { return Math.min(1, this.depth(x, y) / this.edge); }

  /** Eave segments overlapping the view (world px), clipped to it. */
  visibleEaves(view: { sx: number; sy: number; w: number; h: number }): Eave[] {
    const out: Eave[] = [];
    for (const e of this.eaves) {
      if (e.y < view.sy || e.y >= view.sy + view.h) continue;
      const x0 = Math.max(e.x, view.sx), x1 = Math.min(e.x + e.w, view.sx + view.w);
      if (x1 > x0) out.push({ x: x0, y: e.y, w: x1 - x0 });
    }
    return out;
  }

  /** Distance to the first open pixel from (x, y) stepping (dx, dy) through the union, capped at `edge`. */
  private run(x: number, y: number, dx: number, dy: number): number {
    let px = x, py = y, travelled = 0;
    for (let guard = 0; guard < 32; guard++) {
      const r = this.at(px, py);
      if (!r) return travelled;
      if (dx > 0) { const nx = r.x + r.w; travelled += nx - px; px = nx; }
      else if (dx < 0) { const nx = r.x - 1; travelled += px - nx; px = nx; }
      else if (dy > 0) { const ny = r.y + r.h; travelled += ny - py; py = ny; }
      else { const ny = r.y - 1; travelled += py - ny; py = ny; }
      if (travelled >= this.edge) return this.edge;
    }
    return this.edge;
  }
  private at(x: number, y: number): Rect | null {
    for (const r of this.rects) if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return r;
    return null;
  }
}

/** Bottom edges of the union of `rects`: for each rect the row below it, minus spans another rect covers there. */
export function exposedEaves(rects: readonly Rect[]): Eave[] {
  const out: Eave[] = [];
  for (const r of rects) {
    const y = r.y + r.h;
    let spans: [number, number][] = [[r.x, r.x + r.w]];
    for (const o of rects) {
      if (o === r || y < o.y || y >= o.y + o.h) continue;
      const next: [number, number][] = [];
      for (const [a, b] of spans) {
        if (o.x + o.w <= a || o.x >= b) { next.push([a, b]); continue; }
        if (o.x > a) next.push([a, o.x]);
        if (o.x + o.w < b) next.push([o.x + o.w, b]);
      }
      spans = next;
      if (!spans.length) break;
    }
    for (const [a, b] of spans) if (b > a) out.push({ x: a, y, w: b - a });
  }
  out.sort((p, q) => p.y - q.y || p.x - q.x);
  return out;
}
